import postFatigue from './assets/post-fatigue.jpg'
import postHero from './assets/post-hero.jpg'
import postIntent from './assets/post-intent.jpg'

export type BlogFigure = {
  src: string
  alt: string
  caption?: string
}

export type BlogChartId = 'okcupid-below-medium' | 'okcupid-message-share' | 'bruch-aspirational'

export type BlogBlock =
  | { type: 'p'; text: string }
  | { type: 'figure'; figure: BlogFigure }
  | { type: 'chart'; chart: BlogChartId }
  | { type: 'sources'; items: { label: string; href: string }[] }

export type BlogPost = {
  slug: string
  title: string
  date: string
  excerpt: string
  hero?: BlogFigure
  body: BlogBlock[]
}

/**
 * Single essay for launch. Expand when a real CMS lands.
 */
export const posts: BlogPost[] = [
  {
    slug: 'a-match-not-a-feed',
    title: 'A match, not a feed',
    date: '2026-08-01',
    excerpt:
      'Swipe apps gave people more profiles than ever and somehow less dating. Why the feed fails — including the lopsided math it runs on — and what we built instead.',
    hero: {
      src: postHero,
      alt: 'A couple laughing together on a patio at golden hour',
      caption:
        'What people actually want is an evening with someone who fits — not another stack of profiles.',
    },
    body: [
      {
        type: 'p',
        text: 'Somewhere in the last ten years, “work on your dating life” started to mean open an app and sort. Profiles, filters, a thumb that never has to rest. The apps got slicker every year. Plenty of people still left the night feeling lonelier than when they opened the thing — a strange outcome for a tool that was supposed to help, until you notice it was built like a content feed.',
      },
      {
        type: 'p',
        text: 'Online meeting itself is fine. Most of us met half our friends through a screen at some point. The strange part is treating strangers as stock. A feed doesn’t care whether two lives would work. It cares whether you keep scrolling. A feed is built to be reopened, and a relationship that works is one of the few outcomes that stops you from reopening it.',
      },
      {
        type: 'p',
        text: 'Gen Z is the first cohort that never knew adult dating without that design. Among 18- to 29-year-olds, a lot of people are single, and a lot of those singles aren’t looking — not for a relationship, not even for casual dates. Family-studies researchers have described a “dating recession”: a large share of young adults aren’t dating with any regularity, and many young women and men report few or no dates over a whole year.',
      },
      {
        type: 'p',
        text: 'Romance sits on top of a thinner social life in general. Time-use surveys show young adults spending less time with friends in person than people their age did in the early 2000s. Other surveys find Gen Z more likely than older adults to say they feel lonely. Apps didn’t cause all of that. They’re just one more place where people practice evaluation instead of presence.',
      },
      {
        type: 'figure',
        figure: {
          src: postFatigue,
          alt: 'A young adult alone at a café table, lit by a phone screen at night',
          caption: 'Hours of sorting rarely feel like progress toward a real date.',
        },
      },
      {
        type: 'p',
        text: 'Users keep saying the same thing about swipe products: they promise options and deliver fatigue. In a non-representative online panel for Forbes Health, about four in five people said dating apps left them emotionally, mentally, or physically exhausted at least some of the time. Gen Z reported the worst of it in that survey. If you’ve closed Tinder for the third time this year, you’re in crowded company.',
      },
      {
        type: 'p',
        text: 'Part of the problem is pure load. Too many faces, too little signal. A study of young swipe users found heavy, compulsive swiping went along with comparing yourself upward, fretting about staying single, and freezing under too many choices. That study can’t prove the app caused the mood. Still, the product rewards a way of looking at people that is terrible for deciding who to have dinner with.',
      },
      {
        type: 'p',
        text: 'Heterosexual swipe markets also bend under a lopsided pattern that keeps showing up in the data. Older Tinder-style experiments and later app figures have pointed the same way: men liking a large share of what they see — often near half or more — and women closer to the low teens. The measurements are imperfect and the platforms have changed, but nobody has published a clean study showing the gap closed. Neither side is behaving badly. Liking costs a man almost nothing, so casting wide is rational. Getting a hundred low-effort likes makes ranking them the only sane response, so filtering hard is rational too. Each side’s move makes the other’s worse, and the app takes a cut of the churn.',
      },
      {
        type: 'p',
        text: 'Rating data from OkCupid’s old company blog (Christian Rudder, OkTrends, ~2009) sits in the same neighborhood — not peer-reviewed research, and a single platform. Men rated women’s looks near a normal curve. Women rated about 80% of men as worse-looking than “medium.” Rudder also noted that women’s messaging was less skewed than those ratings, which matters: votes and behavior aren’t the same thing. Where the messages land is the clearer story. About two-thirds of men’s messages went to the best-looking third of women.',
      },
      { type: 'chart', chart: 'okcupid-below-medium' },
      { type: 'chart', chart: 'okcupid-message-share' },
      {
        type: 'p',
        text: 'Elizabeth Bruch and M. E. J. Newman, writing in Science Advances (2018), found a similar upward pull among heterosexual users in four large U.S. cities: both men and women tended to contact people about 25% more desirable than themselves by the study’s measure. Attention piles at the top. Combine that hierarchy with one side liking almost everything and the other side rationing, and the market breaks. A few highly sought profiles drown in weak interest. Everyone else gets silence, or likes that meant nothing in particular. An app that sells attention does fine in that market. An app that has to produce actual dates doesn’t.',
      },
      { type: 'chart', chart: 'bruch-aspirational' },
      {
        type: 'p',
        text: 'Trust is harder in the same environment. Among online daters Pew surveyed, more people said the experience was positive than negative, but a large minority disagreed — and harassment shows up a lot, especially for younger women. Generalized trust in strangers has been soft for a long time among younger adults. Asking people to build intimacy out of a strip of photos and a bio is a steep ask when the inbox is either empty or full of low-effort attention.',
      },
      {
        type: 'p',
        text: 'So what would a saner setup look like? Mostly the inverse of a feed. Be picky about who gets in, instead of showing people whoever signed up last night. Stop mashing a person into one marketable profile — what someone actually is and what they’d accept in a partner are different things, and a match should have to clear both, in both directions. That one rule kills the volume game on its own: nobody profits from spraying likes when neither side hears anything until both sides clear. Learn about people in conversation, over time, rather than from a form they filled out once. And when you barely know someone, say so, instead of dressing a guess up as a score.',
      },
      {
        type: 'p',
        text: 'Revel Club is our attempt at that. You apply. You talk with Rheya, our matchmaker (she happens to be AI). She turns people down when the fit for the club is wrong, learns what you actually care about in conversation, and only introduces people when both of you look like a real mutual match. We check whether their life sits inside what you’ll accept, and whether yours sits inside what they’ll accept. Preferences can bend; must-haves don’t. Both sides have to clear before anyone gets an introduction — no reward for spraying yes across a feed, and no routing half the city toward the same five faces.',
      },
      {
        type: 'figure',
        figure: {
          src: postIntent,
          alt: 'A couple walking a waterfront at sunset, holding hands',
          caption: 'The finish line is a real evening together, not a longer chat thread.',
        },
      },
      {
        type: 'p',
        text: 'We also cap how many introductions you get. Paid members get more than free members, but nobody gets a dump of low-quality matches just to keep the product feeling “active.” Matching is local on purpose: a region only opens when there are enough people nearby that mutual fit can mean something on a Tuesday night. We care less about message volume than about whether you actually go on dates with people who passed the same gate you did.',
      },
      {
        type: 'p',
        text: 'We built this because we got tired of products that treat dating like content. We’d rather interview carefully, keep standards, and send a few introductions that matter than run an endless scroll. Three people you take seriously beat three hundred you forget. If that trade sounds right to you, apply.',
      },
      {
        type: 'sources',
        items: [
          {
            label: 'Pew Research Center — Online dating in the U.S. (2023)',
            href: 'https://www.pewresearch.org/internet/2023/02/02/from-looking-for-love-to-swiping-the-field-online-dating-in-the-u-s/',
          },
          {
            label: 'Pew Research Center — Experiences of U.S. online daters',
            href: 'https://www.pewresearch.org/internet/2023/02/02/the-experiences-of-u-s-online-daters/',
          },
          {
            label: 'Forbes Health — Dating app fatigue survey',
            href: 'https://www.forbes.com/health/dating/dating-app-fatigue/',
          },
          {
            label: 'AEI — Gen Z dating patterns / family-studies reporting',
            href: 'https://www.aei.org/articles/gen-zs-dating-revolution/',
          },
          {
            label: 'American Time Use Survey (BLS)',
            href: 'https://www.bls.gov/tus/',
          },
          {
            label:
              'Bruch & Newman — Aspirational pursuit of mates in online dating markets (Science Advances, 2018)',
            href: 'https://www.science.org/doi/10.1126/sciadv.aap9815',
          },
          {
            label: 'OkCupid / Christian Rudder — Your looks and online dating (archived)',
            href: 'https://web.archive.org/web/20120723173702/http://blog.okcupid.com/index.php/your-looks-and-online-dating/',
          },
        ],
      },
    ],
  },
]
